/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstArrayModel,
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	ISchemaConstObjectModel,
	ISelectionItem_Base,
	isSchemaConstArrayModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaConstObjectModel,
	TGenericBlueprintSchema,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { EditSelection, TEditSelectionItem } from "@adapptio/lib-components";
import { emitEvent } from "@adapptio/lib-shared";

export enum THEME_SELECTION_ITEM_TYPE {
	BLOCK = "block",
	STYLE = "style"
}

export interface ISelectionItem_Block extends ISelectionItem_Base {
	type: THEME_SELECTION_ITEM_TYPE.BLOCK;
	parentListModelId: number;
}

export interface ISelectionItem_Style extends ISelectionItem_Base {
	type: THEME_SELECTION_ITEM_TYPE.STYLE;
	parentMapModelId: number;
	mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>;
}

export type TThemeEditSelectionItem = TEditSelectionItem | ISelectionItem_Block | ISelectionItem_Style;

/**
 * Type guard for block selection
 * @param value
 */
export function isSelectionItemBlock(value: TThemeEditSelectionItem): value is ISelectionItem_Block {
	return value?.type === THEME_SELECTION_ITEM_TYPE.BLOCK;
}

/**
 * Type guard for style selection
 * @param value
 */
export function isSelectionItemStyle(value: TThemeEditSelectionItem): value is ISelectionItem_Style {
	return value?.type === THEME_SELECTION_ITEM_TYPE.STYLE;
}

/**
 * Class to manage view model selections
 */
export class ThemeEditSelection extends EditSelection<TThemeEditSelectionItem> {
	public constructor() {
		super();
	}

	/**
	 * Deselected all
	 */
	public deselectAll() {
		this.unselectAll();
		emitEvent(this.onChange);
	}

	public selectBlock(
		parentListModel: ISchemaConstArrayModel<TGenericBlueprintSchema>,
		model: ISchemaConstObjectModel<TSchemaConstObjectProps>
	): boolean {
		if (!isSchemaConstArrayModel(parentListModel) || !isSchemaConstObjectModel(model)) {
			return false;
		}

		const isSelected = this.isBlockSelected(model);

		// In multiselect, de-selected the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectBlock(model);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Block = {
			type: THEME_SELECTION_ITEM_TYPE.BLOCK,
			parentListModelId: parentListModel.nodeId,
			nodeId: model.nodeId
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if parameter is selected
	 */
	public isBlockSelected(model: ISchemaConstObjectModel<TSchemaConstObjectProps>): boolean {
		if (!isSchemaConstObjectModel(model)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(model.nodeId);
		const item = this.items[index];

		if (isSelectionItemBlock(item)) {
			return item.nodeId === model.nodeId;
		}

		return false;
	}

	public deselectBlock(model: ISchemaConstObjectModel<TSchemaConstObjectProps>): boolean {
		if (!isSchemaConstObjectModel(model)) {
			return false;
		}

		if (!this.isBlockSelected(model)) {
			return false;
		}

		return this.deselectByNodeId(model.nodeId);
	}

	public selectStyle(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel)) {
			return false;
		}

		const isSelected = this.isStyleSelected(parentModel, mapEntry);

		// In multiselect, de-selected the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectStyle(parentModel, mapEntry);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Style = {
			type: THEME_SELECTION_ITEM_TYPE.STYLE,
			nodeId: mapEntry.key.nodeId,
			parentMapModelId: parentModel.nodeId,
			mapEntry: mapEntry
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if parameter is selected
	 */
	public isStyleSelected(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(mapEntry.key.nodeId);
		const item = this.items[index];

		if (isSelectionItemStyle(item)) {
			return item.parentMapModelId === parentModel.nodeId;
		}

		return false;
	}

	public deselectStyle(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		if (!this.isStyleSelected(parentModel, mapEntry)) {
			return false;
		}

		return this.deselectByNodeId(mapEntry.key.nodeId);
	}
}
